import { useMutation, useQueryClient } from "@tanstack/react-query";
import useFetch from "../lib/useFetch";
import { IProductCore } from "../types/products";

const API = process.env.REACT_APP_API;

/**
 * Payload para atualização de produto
 */
export interface UpdateCoreProductPayload {
  name?: string;
  category?: number;
  description?: string;
  unit?: string;
  ingredients?: string;
  badges?: number[];
  refrigerated?: boolean;
  stack_order?: number;
  active?: boolean;
  imageFile?: File | null;
}

/**
 * Hook para atualizar um produto do catálogo (core.Product)
 */
const useUpdateCoreProduct = () => {
  const fetch = useFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: UpdateCoreProductPayload;
    }) => {
      const { imageFile, badges, ...rest } = data;

      // Se há um arquivo de imagem, usar FormData
      if (imageFile instanceof File) {
        const formData = new FormData();

        // Adicionar campos simples
        for (const key in rest) {
          const value = rest[key as keyof typeof rest];
          // Incluir valores definidos (incluindo false e 0, mas não null/undefined/string vazia)
          if (value !== undefined && value !== null) {
            if (typeof value === "boolean") {
              formData.append(key, value.toString());
            } else if (typeof value === "string" && value !== "") {
              formData.append(key, value);
            } else if (typeof value === "number") {
              formData.append(key, value.toString());
            }
          }
        }

        // Adicionar badges (array)
        if (Array.isArray(badges) && badges.length > 0) {
          badges.forEach((badgeId) => {
            formData.append("badges", String(badgeId));
          });
        }

        // Adicionar arquivo de imagem
        formData.append("image", imageFile);

        return fetch(`${API}/api/semeadora/core_products/${id}/`, {
          method: "PATCH",
          body: formData,
          formData: true,
        }) as Promise<IProductCore>;
      }

      // JSON normal (sem arquivo)
      const jsonPayload: any = { ...rest };
      if (Array.isArray(badges)) {
        jsonPayload.badges = badges;
      }

      return fetch(`${API}/api/semeadora/core_products/${id}/`, {
        method: "PATCH",
        body: jsonPayload,
      }) as Promise<IProductCore>;
    },
    onSuccess: (data, variables) => {
      // Invalidar cache da lista
      queryClient.invalidateQueries({ queryKey: ["core-products"] });
      // Invalidar cache do produto específico
      queryClient.invalidateQueries({
        queryKey: ["core_product", variables.id],
      });
    },
    onError: (error) => {
      console.error("Erro ao atualizar core_product", error);
    },
  });
};

export default useUpdateCoreProduct;
